import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus } from "lucide-react";
import { Input } from "../../components/ui/input";
import { adminApi } from "../../lib/api";
import { JOB_STATUSES, statusColor } from "../../lib/jobConstants";
import { money } from "../../lib/jobCalc";

export const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listJobs({ q: q || undefined, status: status || undefined });
      setJobs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-jobs-page">
      <div className="flex items-start sm:items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-black uppercase text-2xl sm:text-3xl md:text-4xl text-[#111619]">Jobs</h1>
          <p className="text-[#3f4b52] mt-1 text-sm sm:text-base">Every fitment from quote to paid.</p>
        </div>
        <Link
          to="/admin/jobs/new"
          data-testid="jobs-new-btn"
          className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs sm:text-sm px-4 sm:px-5 h-10 sm:h-11 rounded-md inline-flex items-center gap-2 hover:brightness-110 transition shadow"
        >
          <Plus size={16} /> <span className="hidden xs:inline">New Job</span><span className="xs:hidden">New</span>
        </Link>
      </div>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by customer code, name, reg, VIN, claim, policy or insurer…"
            className="pl-9 h-11 bg-[#f5f6f6] border-[#cfd7db]"
            data-testid="jobs-search"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-11 bg-[#f5f6f6] border border-[#cfd7db] rounded-md px-3 text-sm"
          data-testid="jobs-status-filter"
        >
          <option value="">All statuses</option>
          {JOB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button
          type="button"
          onClick={load}
          className="h-11 px-5 bg-[#20282c] text-white font-display uppercase tracking-wider text-sm rounded-md"
          data-testid="jobs-filter-apply"
        >
          Apply
        </button>
      </div>

      <div className="mt-6 bg-white border border-[#cfd7db] rounded-md shadow-sm">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#edf2f4] text-left">
              <tr className="font-display uppercase tracking-widest text-[11px] text-[#3f4b52]">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Vehicle</th>
                <th className="px-4 py-3">Reg</th>
                <th className="px-4 py-3">Insurance</th>
                <th className="px-4 py-3">Fitment</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#eef2f4]">
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#3f4b52]">Loading…</td></tr>
              )}
              {!loading && jobs.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#3f4b52]">No jobs match. Try adjusting your filters or create a new one.</td></tr>
              )}
              {jobs.map((j) => {
                const sub = (Number(j.retail) || 0) + (Number(j.fitment_fee) || 0) + (Number(j.moulding) || 0);
                const total = sub * (1 + (Number(j.vat_pct) || 0) / 100);
                return (
                  <tr key={j.id} className="hover:bg-[#fafbfb]" data-testid={`job-row-${j.id}`}>
                    <td className="px-4 py-3 font-display font-bold">#{j.job_number}</td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/jobs/${j.id}`} className="text-[#111619] hover:text-[#f28a16] font-semibold">
                        {j.client_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-[#3f4b52]">{j.vehicle_make_model || "—"}</td>
                    <td className="px-4 py-3 text-[#3f4b52]">{j.registration || "—"}</td>
                    <td className="px-4 py-3 text-[#3f4b52]">
                      {j.payment_type === "cash" ? "Cash" : (j.insurance_company || "—")}
                    </td>
                    <td className="px-4 py-3 text-[#3f4b52]">{j.fitment_date || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold">{money(total)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-wider ${statusColor(j.status)}`}>
                        {j.status}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-[#eef2f4]">
          {loading && (
            <div className="px-4 py-10 text-center text-[#3f4b52] text-sm">Loading…</div>
          )}
          {!loading && jobs.length === 0 && (
            <div className="px-4 py-10 text-center text-[#3f4b52] text-sm">No jobs match. Try adjusting your filters or create a new one.</div>
          )}
          {jobs.map((j) => {
            const sub = (Number(j.retail) || 0) + (Number(j.fitment_fee) || 0) + (Number(j.moulding) || 0);
            const total = sub * (1 + (Number(j.vat_pct) || 0) / 100);
            const isBsg = j.brand === "bsg";
            return (
              <Link
                key={j.id}
                to={`/admin/jobs/${j.id}`}
                data-testid={`job-card-${j.id}`}
                className="block px-4 py-3 hover:bg-[#fafbfb] active:bg-[#f5f6f6]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-bold text-[#111619]">#{j.job_number}</span>
                      {isBsg && (
                        <span className="font-display uppercase tracking-widest text-[10px] px-1.5 py-0.5 rounded bg-[#dbeafe] text-[#1e3a8a]">BSG</span>
                      )}
                      <span className={`px-2 py-0.5 rounded-sm text-[10px] font-display uppercase tracking-wider ${statusColor(j.status)}`}>
                        {j.status}
                      </span>
                    </div>
                    <div className="mt-1 text-sm font-semibold text-[#111619] truncate">{j.client_name}</div>
                    <div className="mt-0.5 text-xs text-[#3f4b52] truncate">
                      {j.vehicle_make_model || "—"} · {j.registration || "—"}
                    </div>
                    <div className="mt-0.5 text-[11px] text-[#3f4b52] truncate">
                      {j.payment_type === "cash" ? "Cash" : (j.insurance_company || "—")}
                      {j.fitment_date ? ` · ${j.fitment_date}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-[#111619]">{money(total)}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminJobs;
